<template>
  <div class="misc-fee-create">
    <el-row :gutter="20">
      <el-col :span="14">
        <el-card shadow="never">
          <div slot="header">
            <span>{{ $t('miscFeeCreate.searchTitle') }}</span>
          </div>
          <el-form :inline="true" :model="condition" size="small" class="query-form">
            <el-form-item :label="$t('miscFeeCreate.searchType')">
              <el-radio-group v-model="condition.searchType">
                <el-radio-button label="roomNum">{{ $t('miscFeeCreate.roomNum') }}</el-radio-button>
                <el-radio-button label="ownerName">{{ $t('miscFeeCreate.ownerName') }}</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item>
              <el-input
                v-model="condition.keyword"
                clearable
                :placeholder="$t('miscFeeCreate.keywordPlaceholder')"
                @keyup.enter.native="queryRoomList"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" icon="el-icon-search" @click="queryRoomList">
                {{ $t('miscFeeCreate.query') }}
              </el-button>
              <el-button icon="el-icon-refresh" @click="resetSearch">
                {{ $t('miscFeeCreate.reset') }}
              </el-button>
            </el-form-item>
          </el-form>

          <el-table v-loading="roomLoading" :data="rooms" border stripe>
            <el-table-column :label="$t('miscFeeCreate.roomName')" min-width="140">
              <template slot-scope="scope">{{ formatRoomName(scope.row) }}</template>
            </el-table-column>
            <el-table-column prop="ownerName" :label="$t('miscFeeCreate.owner')" width="120" />
            <el-table-column prop="floorNum" :label="$t('miscFeeCreate.floor')" width="90" />
            <el-table-column prop="unitNum" :label="$t('miscFeeCreate.unit')" width="90" />
            <el-table-column prop="builtUpArea" :label="$t('miscFeeCreate.area')" width="100" />
            <el-table-column :label="$t('miscFeeCreate.operation')" width="90" fixed="right" align="center">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="selectRoom(scope.row)">
                  {{ $t('miscFeeCreate.choose') }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>

      <el-col :span="10">
        <el-card shadow="never">
          <div slot="header">
            <span>{{ $t('miscFeeCreate.billingTitle') }}</span>
          </div>
          <el-alert :title="$t('miscFeeCreate.configTip')" type="info" :closable="false" show-icon />
          <el-form :model="form" label-width="120px" class="billing-form">
            <el-form-item :label="$t('miscFeeCreate.selectedRoom')">
              <el-input :value="selectedRoomName" disabled />
            </el-form-item>
            <el-form-item :label="$t('miscFeeCreate.selectedOwner')">
              <el-input :value="form.ownerName" disabled />
            </el-form-item>
            <el-form-item :label="$t('miscFeeCreate.feeType')">
              <el-select v-model="form.feeTypeCd" @change="handleFeeTypeChange">
                <el-option
                  v-for="item in miscFeeTypes"
                  :key="item.statusCd"
                  :label="item.name"
                  :value="item.statusCd"
                />
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('miscFeeCreate.feeItem')">
              <el-select v-model="form.configId" filterable @change="handleConfigChange">
                <el-option
                  v-for="item in feeConfigs"
                  :key="item.configId"
                  :label="item.feeName"
                  :value="item.configId"
                />
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('miscFeeCreate.amount')">
              <el-input v-model="form.amount" />
            </el-form-item>
            <el-form-item :label="$t('miscFeeCreate.startTime')">
              <el-date-picker
                v-model="form.startTime"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
              />
            </el-form-item>
            <el-form-item :label="$t('miscFeeCreate.endTime')">
              <el-date-picker
                v-model="form.endTime"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
              />
            </el-form-item>
            <el-form-item :label="$t('miscFeeCreate.remark')">
              <el-input v-model="form.feeRemark" type="textarea" :rows="3" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" :loading="saveLoading" @click="saveMiscFee">
                {{ $t('miscFeeCreate.submit') }}
              </el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { listFeeConfigs, saveRoomCreateFee } from '@/api/fee/roomCreateFeeAddApi'
import { queryRooms, queryRoomsByOwner } from '@/api/fee/miscFeeCreateApi'
import { getCurrentStoreId } from '@/utils/storeUtil'

const MISC_FEE_TYPES = [
  { statusCd: '888800010020', nameKey: 'advertisementFee' },
  { statusCd: '888800010021', nameKey: 'decorationDeposit' },
  { statusCd: '888800010022', nameKey: 'temporaryServiceFee' },
  { statusCd: '888800010023', nameKey: 'venueMiscellaneousFee' }
]

export default {
  name: 'MiscFeeCreateList',
  data() {
    return {
      roomLoading: false,
      saveLoading: false,
      condition: {
        searchType: 'roomNum',
        keyword: ''
      },
      rooms: [],
      selectedRoom: null,
      feeConfigs: [],
      form: this.getDefaultForm()
    }
  },
  computed: {
    miscFeeTypes() {
      return MISC_FEE_TYPES.map(item => ({
        statusCd: item.statusCd,
        name: this.$t(`miscFeeCreate.${item.nameKey}`)
      }))
    },
    selectedRoomName() {
      return this.selectedRoom ? this.formatRoomName(this.selectedRoom) : ''
    }
  },
  created() {
    this.handleFeeTypeChange(this.form.feeTypeCd)
  },
  methods: {
    getDefaultForm() {
      return {
        roomId: '',
        ownerName: '',
        feeTypeCd: '888800010020',
        configId: '',
        amount: '',
        startTime: this.formatDateTime(new Date()),
        endTime: this.formatDateTime(this.addDay(new Date(), 1)),
        computingFormula: '',
        feeRemark: ''
      }
    },
    formatDateTime(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    addDay(date, days) {
      const newDate = new Date(date)
      newDate.setDate(newDate.getDate() + days)
      return newDate
    },
    formatRoomName(room) {
      if (!room) {
        return ''
      }
      if (room.roomName) {
        return room.roomName
      }
      if (room.floorNum && room.unitNum && room.roomNum) {
        return `${room.floorNum}-${room.unitNum}-${room.roomNum}`
      }
      if (room.floorNum && room.roomNum) {
        return `${room.floorNum}-${room.roomNum}`
      }
      return room.roomNum || ''
    },
    async queryRoomList() {
      if (!this.condition.keyword) {
        this.$message.warning(this.$t('miscFeeCreate.keywordRequired'))
        return
      }
      this.roomLoading = true
      try {
        const params = {
          page: 1,
          row: 20,
          communityId: getCommunityId()
        }
        const res = this.condition.searchType === 'roomNum'
          ? await queryRooms({ ...params, flag: 1, roomNum: this.condition.keyword })
          : await queryRoomsByOwner({ ...params, ownerNameLike: this.condition.keyword })
        this.rooms = res.rooms || []
        if (this.rooms.length <= 0) {
          this.$message.warning(this.$t('miscFeeCreate.noRoomFound'))
        }
      } catch (error) {
        console.error('查询房屋失败:', error)
      } finally {
        this.roomLoading = false
      }
    },
    resetSearch() {
      this.condition.keyword = ''
      this.rooms = []
      this.selectedRoom = null
      this.form = this.getDefaultForm()
      this.feeConfigs = []
      this.handleFeeTypeChange(this.form.feeTypeCd)
    },
    selectRoom(room) {
      this.selectedRoom = room
      this.form.roomId = room.roomId
      this.form.ownerName = room.ownerName || ''
    },
    async handleFeeTypeChange(feeTypeCd) {
      this.form.configId = ''
      this.form.computingFormula = ''
      await this.loadFeeConfigs(feeTypeCd)
    },
    async loadFeeConfigs(feeTypeCd) {
      try {
        const res = await listFeeConfigs({
          page: 1,
          row: 500,
          communityId: getCommunityId(),
          feeTypeCd,
          isDefault: 'F',
          state: 'Y',
          valid: '1'
        })
        this.feeConfigs = res.feeConfigs || []
        if (this.feeConfigs.length > 0) {
          this.form.configId = this.feeConfigs[0].configId
          this.handleConfigChange(this.form.configId)
        }
      } catch (error) {
        console.error('加载杂项费费用项失败:', error)
      }
    },
    handleConfigChange(configId) {
      const config = this.feeConfigs.find(item => item.configId === configId)
      if (config) {
        this.form.computingFormula = config.computingFormula
      }
    },
    validateForm() {
      if (!this.form.roomId) {
        this.$message.warning(this.$t('miscFeeCreate.noRoomSelected'))
        return false
      }
      if (!this.form.feeTypeCd) {
        this.$message.warning(this.$t('miscFeeCreate.feeTypeRequired'))
        return false
      }
      if (!this.form.configId) {
        this.$message.warning(this.$t('miscFeeCreate.feeItemRequired'))
        return false
      }
      if (!this.form.amount) {
        this.$message.warning(this.$t('miscFeeCreate.amountRequired'))
        return false
      }
      if (!this.form.startTime) {
        this.$message.warning(this.$t('miscFeeCreate.startTimeRequired'))
        return false
      }
      if (!this.form.endTime) {
        this.$message.warning(this.$t('miscFeeCreate.endTimeRequired'))
        return false
      }
      if (new Date(this.form.startTime).getTime() >= new Date(this.form.endTime).getTime()) {
        this.$message.warning(this.$t('miscFeeCreate.endTimeMustAfterStart'))
        return false
      }
      return true
    },
    async saveMiscFee() {
      if (!this.validateForm()) {
        return
      }
      this.saveLoading = true
      try {
        const storeId = await getCurrentStoreId()
        if (!storeId) {
          this.$message.warning(this.$t('miscFeeCreate.storeRequired'))
          return
        }
        const res = await saveRoomCreateFee({
          communityId: getCommunityId(),
          locationObjId: this.form.roomId,
          configId: this.form.configId,
          storeId,
          amount: this.form.amount,
          startTime: this.form.startTime,
          endTime: this.form.endTime,
          feeTypeCd: this.form.feeTypeCd,
          feeRemark: this.form.feeRemark
        })
        if (res.code !== undefined && res.code != 0) {
          this.$message.error(res.msg)
          return
        }
        this.$message.success(this.$t('miscFeeCreate.saveSuccess'))
        this.form = this.getDefaultForm()
        this.form.roomId = this.selectedRoom ? this.selectedRoom.roomId : ''
        this.form.ownerName = this.selectedRoom ? this.selectedRoom.ownerName || '' : ''
        await this.handleFeeTypeChange(this.form.feeTypeCd)
      } catch (error) {
        console.error('杂项费开单失败:', error)
      } finally {
        this.saveLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.misc-fee-create {
  min-height: calc(100vh - 84px);
  padding: 20px;
  background: #f5f7fa;
}

.query-form {
  margin-bottom: 10px;
}

.billing-form {
  margin-top: 15px;
}

.billing-form ::v-deep .el-select,
.billing-form ::v-deep .el-date-editor {
  width: 100%;
}
</style>
